import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { DualLangInputComponent } from '../../../../../../../../../form-builder/lib/dual-lang-input/dual-lang-input.component';
import { PositionService } from '../../../../../positions/position.service';
import { SelectComponent } from '../../../../../../../../../ui-components/lib/select/select.component';
import { PaginatedSelectComponent } from '../../../../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { InputComponent } from '../../../../../../../../../ui-components/lib/input/input.component';
import { DatePickerComponent } from '../../../../../../../../../ui-components/lib/date-picker/date-picker.component';
import { ValidationMessageComponent } from '../../../../../../../../../ui-components/lib/validation-message/validation-message.component';
import { OptionItem } from '../../../../../../../../../ui-components/lib/option.interface';
import { LookupOptionItem } from '../../../../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { FormService } from '../../../../../../../../../form-builder/services/form/form.service';
import { dualLanguageCustomValidators } from '../../../../../../../../../form-builder/services/form/custom-validators';
import { FormValidation } from '../../../../../../../../../ui-components/enums/form-validation';
import { ToasterService } from '../../../../../../../../../ui-components/services/toast/toast.service';
import { ENDPOINTS } from '../../../../../../../../../core/common/constants/endpoints.constants';
import { QuickCreatedItem } from './quick-create-model';

@Component({
  selector: 'lib-position-quick-create-form',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    DualLangInputComponent,
    SelectComponent,
    PaginatedSelectComponent,
    InputComponent,
    DatePickerComponent,
    ValidationMessageComponent,
  ],
  templateUrl: './position-quick-create-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PositionQuickCreateFormComponent implements OnInit {
  private readonly positionService = inject(PositionService);
  private readonly formService = inject(FormService);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  readonly legalEntity = input.required<OptionItem[]>();
  readonly prefillName = input<string>('');

  readonly created = output<LookupOptionItem>();
  readonly cancelled = output<void>();

  readonly isLoading = signal(false);

  protected readonly departmentApi = `${ENDPOINTS.Lookup.Departments}?filters[state]=active`;
  protected readonly jobTitleApi = `${ENDPOINTS.Lookup.JobTitles}?filters[state]=active`;
  protected readonly reportingPositionApi = `${ENDPOINTS.Lookup.Positions}?filters[state]=active`;

  readonly formGroup = new FormGroup({
    name: new FormGroup(
      {
        en: new FormControl('', {
          nonNullable: true,
          validators: [Validators.maxLength(FormValidation.maxLength)],
        }),
        ar: new FormControl('', {
          nonNullable: true,
          validators: [Validators.maxLength(FormValidation.maxLength)],
        }),
      },
      { validators: [dualLanguageCustomValidators.atLeastOneLangRequired] }
    ),
    identifier: new FormControl<string | null>(null, [Validators.maxLength(100)]),
    legal_entity_id: new FormControl<number | null>(null, Validators.required),
    department_id: new FormControl<number | null>(null, Validators.required),
    job_title_id: new FormControl<number | null>(null, Validators.required),
    reports_to_position_id: new FormControl<number | null>(null),
    fte: new FormControl<number | null>(null, [
      Validators.min(0),
      Validators.max(1),
    ]),
    effective_from: new FormControl<string | null>(null),
    effective_to: new FormControl<string | null>(null),
  });

  ngOnInit(): void {
    if (this.prefillName()) {
      this.formGroup.get('name.en')?.setValue(this.prefillName());
    }
  }

  submit(): void {
    this.formService.syncDualLangControls(this.formGroup);

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);

    this.positionService
      .createPosition(this.formGroup.getRawValue())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: object) => {
          this.isLoading.set(false);
          const data = (response as { data: QuickCreatedItem }).data;
          this.created.emit({
            id: data.id,
            name: data.name,
            value: data.id,
            label: data.name,
          });
        },
        error: error => this.handleError(error),
      });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  private handleError(error: {
    errors?: { [key: string]: string[] };
    message?: string;
    error?: { message?: string };
  }): void {
    this.isLoading.set(false);
    this.formService.addParentFormGroupErrors(
      error as { errors: { [key: string]: string[] }; message: string },
      this.formGroup
    );
    this.toasterService.add(
      error.message ? error.message : (error.error?.message ?? ''),
      '',
      '',
      'error',
      true,
      true
    );
  }
}
